import { hasStudentAccessToSchoolSubject } from 'common/utils/student.subject.access.utils';
import type { Components } from '@/api/generated/api';
import type { IOnboardingStudent } from '@/interfaces';
import { assertNotNullOrUndefined } from './assertion.utils';

/** A new row of the add-students form, empty or with the names of an imported list. */
export function createDraftStudent(names?: { firstName: string; lastName: string }): IOnboardingStudent {
  return {
    id: null,
    clientKey: crypto.randomUUID(),
    firstName: names?.firstName ?? '',
    lastName: names?.lastName ?? '',
    avatar: '',
    avatarPictureFile: null,
    avatarPictureId: null,
    clearAvatarPicture: false,
    existing: false,
  };
}

export function ensureStudentClientKey(student: IOnboardingStudent): string {
  student.clientKey ??= crypto.randomUUID();
  return student.clientKey;
}

/** Record committed IDs before uploads or query refreshes can fail. */
export function applyCreatedStudents(
  drafts: IOnboardingStudent[],
  created: Components.Schemas.CreatedStudentDto[],
): void {
  const byKey = new Map(created.map((student) => [student.clientKey, student]));
  for (const draft of drafts) {
    const student = byKey.get(draft.clientKey);
    assertNotNullOrUndefined(student, 'Missing created student for draft');
    draft.id = student.id;
    draft.existing = true;
    draft.avatarPictureId = student.avatarPictureId;
  }
}

interface IStudentSubjectAccess {
  schoolClassRelation?: { subjects: { schoolSubjectId: number }[] } | null;
}

interface IStudentIdentity {
  id?: number;
}

/** Adapts the frontend student DTO shape to the shared subject-access rule. */
export function isStudentEligibleForSubject(student: IStudentSubjectAccess, schoolSubjectId: number): boolean {
  return hasStudentAccessToSchoolSubject(student.schoolClassRelation?.subjects, schoolSubjectId);
}

/** First names for small groups (≤3); null above that, so the caller shows a count instead. */
export const MAX_NAMED_STUDENTS = 3;
export function namedStudentFirstNames(students: { firstName?: string | null }[]): string[] | null {
  if (!students.length || students.length > MAX_NAMED_STUDENTS) return null;
  const firstNames = students
    .map((student) => student.firstName?.trim())
    .filter((firstName): firstName is string => !!firstName);
  // All or nothing: naming only the students who happen to have a first name would understate how
  // many recipients there are, so a partially nameable group falls back to a count.
  return firstNames.length === students.length ? firstNames : null;
}

/**
 * Student view: the recipient to name in full. Null when there is no student context, or when that
 * student is not a recipient — the caller then shows the regular name/count label.
 */
export function findFocusedStudent<T extends IStudentIdentity>(
  students: T[],
  focusedStudentId: number | null | undefined,
): T | null {
  if (focusedStudentId == null) return null;
  return students.find((student) => student.id === focusedStudentId) ?? null;
}

export function getEligibleStudentsForSubject<T extends IStudentSubjectAccess>(
  students: T[],
  schoolSubjectId: number | null,
): T[] {
  if (schoolSubjectId == null) return students;
  return students.filter((student) => isStudentEligibleForSubject(student, schoolSubjectId));
}

export function hasMissingEligibleStudents<TSelected extends IStudentIdentity, TEligible extends IStudentIdentity>(
  selectedStudents: TSelected[],
  eligibleStudents: TEligible[],
): boolean {
  const selectedIds = new Set(selectedStudents.map((student) => student.id).filter((id): id is number => id != null));
  return eligibleStudents.some((student) => student.id != null && !selectedIds.has(student.id));
}

export function areAllEligibleStudentsSelected<TSelected extends IStudentIdentity, TEligible extends IStudentIdentity>(
  selectedStudents: TSelected[],
  eligibleStudents: TEligible[],
): boolean {
  return eligibleStudents.length > 0 && !hasMissingEligibleStudents(selectedStudents, eligibleStudents);
}

/**
 * A selection is the whole class only when it contains the current class roster exactly.
 * Subject eligibility must never be used as the reference set here: selecting every
 * student of a restricted subject is "all eligible students", not "the whole class".
 */
export function isWholeClassStudentSelection<TSelected extends IStudentIdentity, TClass extends IStudentIdentity>(
  selectedStudents: TSelected[],
  classStudents: TClass[],
): boolean {
  if (!classStudents.length || selectedStudents.length !== classStudents.length) return false;

  const selectedIds = new Set(selectedStudents.map((student) => student.id).filter((id): id is number => id != null));
  if (selectedIds.size !== selectedStudents.length) return false;

  return classStudents.every((student) => student.id != null && selectedIds.has(student.id));
}

/** A one-student roster is clearer when represented by that student's identity. */
export function shouldPresentStudentSelectionAsWholeClass<
  TSelected extends IStudentIdentity,
  TClass extends IStudentIdentity,
>(selectedStudents: TSelected[], classStudents: TClass[]): boolean {
  return classStudents.length > 1 && isWholeClassStudentSelection(selectedStudents, classStudents);
}

/** Materializes "all eligible" without discarding already selected students outside the eligible roster. */
export function resolveStudentSelection<T extends IStudentIdentity>(params: {
  allEligibleStudentsSelected: boolean;
  eligibleStudents: T[];
  selectedStudents: T[];
}): T[] {
  if (!params.allEligibleStudentsSelected) return params.selectedStudents;

  const eligibleIds = new Set(
    params.eligibleStudents.map((student) => student.id).filter((id): id is number => id != null),
  );
  const additionalStudents = params.selectedStudents.filter(
    (student) => student.id == null || !eligibleIds.has(student.id),
  );
  return [...params.eligibleStudents, ...additionalStudents];
}
